import { Flex, Text } from "@mantine/core";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import type { StepFormStepId } from "@/api/service-provider/serviceProvider.endpoints";
import type { IAlignmentApplicantInfoFormProps } from "@/components/data/alignmentApplicantInfoForm";
import JsonFormBuilder from "@/components/features/JsonFormBuilder/JsonFormBuilder";
import W2Coverage from "@/components/features/ServiceProviders/W2Coverage/W2Coverage";
import { buildW2SummaryData } from "@/components/features/SummaryComponent/buildW2SummaryData";
import SummaryComponent from "@/components/features/SummaryComponent/SummaryComponent";
import Loader from "@/components/ui/Loader/Loader";
import {
  useServiceProviderForm,
  useServiceProviderJsonForm,
} from "@/hooks/service-provider/useServiceProviderQueries";
import { useServiceProviderOnboardingStore } from "@/store/useServiceProviderOnboardingStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { relaxRequiredFields } from "@/utils/relaxRequiredFields";
import type { IStepsSchema } from "@/types/common.types";

export const Route = createFileRoute(
  "/service-providers/onboarding/_onboardingLayout/$providerType/$stepId/",
)({
  component: OnboardingStep,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

// this is a temporary function will be modified/removed while actual integration
const keysToSlugsMap = {
  ownerInformation: "personal-info",
  applicantInformation: "application-info",
  businessInformation: "business-info",
  trades: "trades",
  rates: "rates",
  payments: "payment-info",
  coverage: "coverage",
  compensation: "compensation",
  licensesAndCertifications: "licenses-certificates",
};

function OnboardingStep() {
  const { stepId, providerType } = Route.useParams();
  const navigate = useNavigate();

  const { serviceProviderId } = useServiceProviderOnboardingStore();
  const handleGoToTab = (key: string) => {
    const url = keysToSlugsMap[key as keyof typeof keysToSlugsMap];
    // Navigate to the specified onboarding step
    const baseUrl = `/service-providers/onboarding/${providerType}/`;
    const fullUrl = url.startsWith(baseUrl) ? url : `${baseUrl}${url}`;
    navigate({ to: fullUrl });
  };
  const { data: serviceProviderForm, isLoading, error } = useServiceProviderForm(providerType);

  const {
    data: serviceProviderJsonForm,
    isLoading: isStepLoading,
    error: stepError,
  } = useServiceProviderJsonForm(stepId as StepFormStepId, serviceProviderId ?? (null as any));

  // Navigate to first step if no serviceProviderId (only for personal-info step)
  // For other steps, allow navigation even without serviceProviderId initially
  // as it will be set after personal-info is submitted
  useEffect(() => {
    // Only redirect if we're on a step that requires serviceProviderId and it's missing
    // personal-info doesn't require serviceProviderId, but other steps do
    if (!serviceProviderId && serviceProviderForm?.steps && stepId !== "personal-info") {
      const firstStep = serviceProviderForm.steps.find((step) => step.stepNumber === 1);
      if (firstStep) {
        navigate({
          to: "/service-providers/onboarding/$providerType/$stepId",
          params: { providerType, stepId: firstStep.stepKey },
          replace: true,
        });
      }
    }
  }, [serviceProviderId, stepId, providerType, navigate, serviceProviderForm]);

  if (isStepLoading) return <Text>Loading...</Text>;
  if (stepError) return <Text>Error loading data</Text>;

  if (stepId !== "personal-info" && !serviceProviderJsonForm) {
    return <Text>No Data found</Text>;
  }

  if (isLoading) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "50vh" }}>
        <Loader size="small" />
      </Flex>
    );
  }
  if (error) return <Text>Error loading data</Text>;
  if (!serviceProviderForm || !serviceProviderForm.steps) return <Text>No Data found</Text>;

  const stepIndex = serviceProviderForm.steps.findIndex((step) => step.stepKey === stepId);

  const rawStepConfig =
    stepId == "personal-info" && !serviceProviderJsonForm
      ? serviceProviderForm.steps[stepIndex]
      : serviceProviderJsonForm;

  const stepConfig = rawStepConfig ? relaxRequiredFields(rawStepConfig) : rawStepConfig;

  if (!stepConfig) {
    return <Text>Invalid step: {stepId}</Text>;
  }

  const renderStep = (
    _providerType: string,
    stepConfig: IStepsSchema | IAlignmentApplicantInfoFormProps,
  ) => {
    if (providerType === "w2" && stepId === "coverage") {
      return <W2Coverage sourceId={serviceProviderId!} />;
    } else {
      return <JsonFormBuilder jsonFromSchema={stepConfig} />;
    }
  };

  return (
    <>
      {stepId === "summary" ? (
        <SummaryComponent
          summaryData={
            providerType === "w2"
              ? buildW2SummaryData(serviceProviderJsonForm)
              : serviceProviderJsonForm
          }
          handleGoToTab={handleGoToTab}
          showNotesCard={false}
        />
      ) : (
        renderStep(providerType, stepConfig)
      )}
    </>
  );
}
